import { useRef } from 'react';
import { IS_ENCRYPTION_ENABLED } from 'src/config';
import useMutation from 'src/hooks/useMutation';
import { getEncryptParams } from 'src/keycloak';
import { useCreateUploadSessionMutation } from 'src/newServices/uploadSessions';
import { UPLOAD_TYPE } from 'src/types/enum';
import { ErrorContext } from 'src/utils/errorMappings';
import {
  decodeBinaryResponse,
  type SessionCreateResponse
} from 'src/utils/files/serverStateDecoder';
import {
  buildSessionPayload,
  compressManifest
} from 'src/utils/files/uploadSessionManifest';
import { compressEncryptPool } from 'src/utils/workers/compressEncryptPool';
interface SessionInfo {
  sessionId: number | null;
  incomingFileMap: Map<string, number>;
}

const EMPTY_SESSION: SessionInfo = { sessionId: null, incomingFileMap: new Map() };

/**
 * Encapsulates upload-session lifecycle: manifest compression, encryption,
 * session creation, and response decoding.
 *
 * Any upload flow that needs server-side session tracking can compose this
 * hook alongside `useUploadDocuments` instead of owning session internals
 * directly.
 */
export function useUploadSession(parentId: string | number | undefined) {
  const parentIdRef = useRef(parentId);
  parentIdRef.current = parentId;

  const sessionInfoRef = useRef<SessionInfo>({ ...EMPTY_SESSION });

  const [createUploadSession] = useMutation({
    api: useCreateUploadSessionMutation,
    errorContext: ErrorContext.FILES
  });

  /**
   * Compresses + encrypts a file manifest, calls createUploadSession, and
   * populates the internal session ref on success.
   *
   * Returns `{ ok: true }` when the session was created and the ref is ready.
   * Returns `{ ok: false, isConflict: true }` on a 409 so the caller can show
   * its own conflict UI.
   */
  const createSession = async (
    files: File[],
    transactionId: string,
    uploadType?: UPLOAD_TYPE,
    overwrite?: boolean
  ): Promise<{ ok: boolean; isConflict: boolean }> => {
    const gzipFile = await compressManifest(files);

    const encKey = IS_ENCRYPTION_ENABLED ? getEncryptParams()?.k : null;
    const encrypted = await compressEncryptPool.process(
      gzipFile,
      encKey,
      IS_ENCRYPTION_ENABLED
    );
    const manifestFile = encrypted.buffer
      ? new File([encrypted.buffer], 'files_manifest', {
          type: 'application/gzip'
        })
      : gzipFile;

    const sessionPayload = buildSessionPayload(
      parentIdRef.current,
      files,
      transactionId,
      uploadType,
      overwrite
    );

    let sessionCreationError: any = null;

    const sessionCreated = await new Promise<boolean>((resolve) => {
      createUploadSession({
        params: { params: sessionPayload, manifestFile },
        abortContainer: { abort: () => {} },
        successCallback: async (response: unknown) => {
          try {
            let decoded: SessionCreateResponse | null = null;

            if (response instanceof Blob) {
              decoded = await decodeBinaryResponse<SessionCreateResponse>(response);
            } else if (
              response &&
              typeof response === 'object' &&
              'data' in (response as any)
            ) {
              const data = (response as any).data;
              if (data instanceof Blob) {
                decoded = await decodeBinaryResponse<SessionCreateResponse>(data);
              } else {
                decoded = data as SessionCreateResponse;
              }
            }

            if (decoded?.session?.id) {
              const fileMap = new Map<string, number>();
              for (const f of decoded.files ?? []) {
                fileMap.set(f.rp, f.id);
              }
              sessionInfoRef.current = {
                sessionId: decoded.session.id,
                incomingFileMap: fileMap
              };
              resolve(true);
            } else {
              // eslint-disable-next-line no-console
              console.warn(
                '[UploadSession] decoded response has no session.id:',
                decoded
              );
              resolve(false);
            }
          } catch (err) {
            // eslint-disable-next-line no-console
            console.error('[UploadSession] Failed to decode session response:', err);
            resolve(false);
          }
        },
        errorCallback: (error: unknown) => {
          sessionCreationError = error;
          resolve(false);
        }
      });
    });

    if (!sessionCreated) {
      const isConflict =
        sessionCreationError?.status === 409 ||
        sessionCreationError?.originalStatus === 409;
      return { ok: false, isConflict };
    }

    return { ok: true, isConflict: false };
  };

  /**
   * Snapshot of the current session as a flat bag ready to pass as
   * per-call `extraFormDataArgs` to `uploadGroupDocs`/`retryFailedFiles`.
   * `incomingFileMap` is extracted by `getFormDataNew` before FormData iteration.
   * Returns `undefined` when no session exists.
   */
  const getSessionData = (): (Record<string, any> & { incomingFileMap: Map<string, number> }) | undefined => {
    const { sessionId, incomingFileMap } = sessionInfoRef.current;
    if (!sessionId) return undefined;
    return { session_id: sessionId, incomingFileMap };
  };

  const getSessionId = (): number | null => sessionInfoRef.current.sessionId;

  /** Clears stored session data — call before starting a new upload batch. */
  const resetSession = () => {
    sessionInfoRef.current = { sessionId: null, incomingFileMap: new Map() };
  };

  return { createSession, getSessionData, getSessionId, resetSession };
}
